import { useState, type ReactNode } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { getErrorMessage } from '@/lib/error-messages'
import { votePoll, type MyPoll } from '@/lib/polls-api'
import { queryKeys } from '@/lib/query-keys'

const OWN_OPTION = '__own__'

type PollVoteFormProps = {
  poll: MyPoll
  // Shows the own selection without allowing changes (ended polls).
  readOnly?: boolean
  // Rendered next to the submit button, e.g. "Später" in the dialog.
  actions?: ReactNode
  onVoteStart?: () => void
  onVoted?: () => void
}

// Remounted whenever the stored vote changes, so the form always starts from
// the server state (including a participant option that was just created).
export function PollVoteForm(props: PollVoteFormProps) {
  return <PollVoteFormFields key={`${props.poll.id}:${props.poll.my_option_ids.join(',')}`} {...props} />
}

function PollVoteFormFields({ poll, readOnly = false, actions, onVoteStart, onVoted }: PollVoteFormProps) {
  const queryClient = useQueryClient()
  const [selected, setSelected] = useState<string[]>(poll.my_option_ids)
  const [ownSelected, setOwnSelected] = useState(false)
  const [ownLabel, setOwnLabel] = useState('')

  const canAddOwn = poll.allow_participant_options && !readOnly
  const trimmedOwnLabel = ownLabel.trim()
  const unchanged =
    !ownSelected &&
    selected.length === poll.my_option_ids.length &&
    selected.every((id) => poll.my_option_ids.includes(id))
  const hasSelection = selected.length > 0 || (ownSelected && trimmedOwnLabel !== '')

  const voteMutation = useMutation({
    mutationFn: () =>
      votePoll(poll.id, {
        optionIds: selected,
        newOptions: ownSelected && trimmedOwnLabel ? [trimmedOwnLabel] : [],
      }),
    onMutate: () => onVoteStart?.(),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.myPolls })
      onVoted?.()
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const selectSingle = (value: string) => {
    if (value === OWN_OPTION) {
      setOwnSelected(true)
      setSelected([])
    } else {
      setOwnSelected(false)
      setSelected([value])
    }
  }

  const toggleMultiple = (id: string, checked: boolean) =>
    setSelected((ids) => (checked ? [...ids, id] : ids.filter((value) => value !== id)))

  const changeOwnLabel = (value: string) => {
    setOwnLabel(value)
    if (poll.allow_multiple) {
      setOwnSelected(value.trim() !== '')
    } else if (value.trim() !== '') {
      selectSingle(OWN_OPTION)
    }
  }

  const ownInput = (
    <Input
      value={ownLabel}
      onChange={(event) => changeOwnLabel(event.target.value)}
      placeholder="Eigene Antwort hinzufügen"
      maxLength={200}
      aria-label="Eigene Antwort"
      disabled={voteMutation.isPending}
    />
  )

  return (
    <form
      className="space-y-4"
      onSubmit={(event) => {
        event.preventDefault()
        if (hasSelection) voteMutation.mutate()
      }}
    >
      {poll.allow_multiple ? (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">Mehrere Antworten möglich.</p>
          {poll.options.map((option) => (
            <div key={option.id} className="flex items-center gap-3">
              <Checkbox
                id={`poll-${poll.id}-${option.id}`}
                checked={selected.includes(option.id)}
                onCheckedChange={(checked) => toggleMultiple(option.id, checked === true)}
                disabled={readOnly || voteMutation.isPending}
              />
              <Label htmlFor={`poll-${poll.id}-${option.id}`} className="font-normal leading-snug">
                {option.label}
              </Label>
            </div>
          ))}
          {canAddOwn && (
            <div className="flex items-center gap-3">
              <Checkbox
                checked={ownSelected}
                onCheckedChange={(checked) => setOwnSelected(checked === true)}
                disabled={voteMutation.isPending || trimmedOwnLabel === ''}
                aria-label="Eigene Antwort auswählen"
              />
              {ownInput}
            </div>
          )}
        </div>
      ) : (
        <RadioGroup
          value={ownSelected ? OWN_OPTION : (selected[0] ?? '')}
          onValueChange={selectSingle}
          disabled={readOnly || voteMutation.isPending}
        >
          {poll.options.map((option) => (
            <div key={option.id} className="flex items-center gap-3">
              <RadioGroupItem value={option.id} id={`poll-${poll.id}-${option.id}`} />
              <Label htmlFor={`poll-${poll.id}-${option.id}`} className="font-normal leading-snug">
                {option.label}
              </Label>
            </div>
          ))}
          {canAddOwn && (
            <div className="flex items-center gap-3">
              <RadioGroupItem value={OWN_OPTION} aria-label="Eigene Antwort auswählen" />
              {ownInput}
            </div>
          )}
        </RadioGroup>
      )}

      {!readOnly && (
        <div className="flex flex-wrap justify-end gap-2">
          {actions}
          <Button type="submit" disabled={!hasSelection || unchanged || voteMutation.isPending}>
            {poll.has_voted ? 'Stimme ändern' : 'Abstimmen'}
          </Button>
        </div>
      )}
    </form>
  )
}
